import { AccountType } from '@/types';
import { routes } from '@/config/routes';

// Same value as `ROUTE_ID` in '@/config/constants' — not imported because that
// module reads `import.meta.env` at load time, which breaks node-env jest.
const ROUTE_ID = ':id';

/**
 * Unified account detail routing.
 *
 * "Partner Accounts" and "End Customers" are the same `Account` table; the old
 * split sent END_CUSTOMER rows to `/cc/end-customers/:id` and everything else
 * to `/cc/accounts/:id`, so one client had two disjoint detail pages. Every
 * link site now goes through here so the invariant lives in one place:
 * accountType no longer picks the page — the signature keeps it so call sites
 * can pass the record straight through.
 */
export const accountDetailPath = (account: { id: string; accountType?: AccountType }): string =>
    routes.comandCenter.accounts.details.replace(ROUTE_ID, account.id);

/**
 * Target for the "Accounts (New)" tab — the Account Profile page, at its own
 * `/cc/accounts-new/:id` route (the legacy `/cc/accounts/:id` route above
 * keeps serving the restored legacy Account Details page). Same signature and
 * the same accountType-agnostic behaviour as `accountDetailPath`.
 */
export const accountProfilePath = (account: { id: string; accountType?: AccountType }): string =>
    routes.comandCenter.accountsNew.details.replace(ROUTE_ID, account.id);

/**
 * Target for the legacy `/cc/end-customers/:id` route. Preserves the query
 * string (the operational tabs read `?tab=`) so old deep links land on the
 * same tab of the unified page.
 */
export const legacyEndCustomerRedirect = (
    id: string | undefined,
    search: string,
): { pathname: string; search: string } => {
    if (!id) {
        return { pathname: routes.comandCenter.accounts.root, search: '' };
    }
    return { pathname: accountDetailPath({ id }), search };
};
